import { z } from 'zod';

import { UuidSchema } from '../id/id';

/**
 * Keyset ("cursor") pagination, for lists an INSERT can walk into.
 *
 * ## Why this exists beside `pagination.ts` rather than replacing it
 *
 * Offset pagination answers "rows 20–40 of the current ordering", and that is
 * the right question for an admin table over data nobody is writing to while it
 * is read. It is the **wrong** question for a live timeline: a message that
 * arrives between page 1 and page 2 shifts every offset by one, so the member
 * scrolling back sees the last row of page 1 again at the top of page 2 — and a
 * deletion in the same window makes a row disappear unseen. Neither is an error
 * anything reports; both are just wrong.
 *
 * A keyset cursor names a **position in the ordering** instead of a distance
 * from its start, so insertions and deletions elsewhere cannot move it. The cost
 * is that there is no page number and no total, which is exactly right for a
 * timeline and exactly wrong for an admin table — hence two mechanisms, each
 * used where its trade is the good one.
 *
 * ## The position is the row's ID, and that is not a shortcut
 *
 * The obvious keyset for a timeline is `(created_at, id)` — a timestamp with the
 * id breaking ties. In **this** workspace that would be strictly worse, for a
 * reason that is easy to miss and impossible to see in a unit test:
 *
 * - Every table's primary key is a **UUIDv7 minted app-side by the one clock**
 *   (`pk()` in `timestamps.helper.ts`). UUIDv7 is time-ordered and unique, so id
 *   order **is** creation order, totally and with no ties to break.
 * - Timestamps, meanwhile, do not survive the read pipeline intact: a stored
 *   `timestamp` carries microseconds, and the repository re-emits it through
 *   `dayjs.format()`, which is **second-precision**. A cursor built from a
 *   timestamp a client was given is therefore rounded *down* to the second — and
 *   a `sent_at < cursor` predicate then re-serves every row from the anchor's own
 *   second on the next page. Two members typing at once produce that routinely,
 *   and the symptom is duplicated messages rather than an error.
 *
 * So the cursor is an id. It is exact, it is one column, it matches the index,
 * and it cannot be degraded by how a timestamp is formatted on the way out.
 */

/**
 * The **coded** default page size (U9's last resort).
 *
 * Deliberately not applied by {@link makeCursorQuery}: a `.default()` in the
 * request schema would mean the server never sees "the client did not say", and
 * a resource whose page size is an operator setting could never consult it — the
 * setting would be configurable in the admin and read by nothing. So `limit`
 * stays optional on the wire, and each resource decides what an absent one
 * means. This is the value such a setting's own coded default should be.
 */
export const DEFAULT_CURSOR_LIMIT = 30;

/**
 * The hard ceiling on one cursor page. Rejected rather than clamped, for
 * `MAX_PAGE_SIZE`'s reason (N7).
 */
export const MAX_CURSOR_LIMIT = 100;

const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * base64url, hand-rolled over ASCII.
 *
 * **Not `Buffer`, and not `btoa`.** This module is imported by `apps/mobile`,
 * whose Hermes runtime provides neither — `Buffer` is a Node global and `btoa` a
 * browser one — so reaching for either would turn a scroll into a red box on a
 * device while every test on the machine passed. The payload is a uuid, so it is
 * ASCII by construction; the encoder asserts that rather than assuming it,
 * because a non-ASCII code point would otherwise be truncated into a *different
 * valid* cursor.
 */
function toBase64Url(input: string): string {
  let out = '';

  for (let i = 0; i < input.length; i += 3) {
    const a = input.charCodeAt(i);
    const b = i + 1 < input.length ? input.charCodeAt(i + 1) : 0;
    const c = i + 2 < input.length ? input.charCodeAt(i + 2) : 0;

    if (a > 0x7f || b > 0x7f || c > 0x7f) {
      throw new Error('A cursor payload must be ASCII.');
    }

    const triple = (a << 16) | (b << 8) | c;
    const chars = [
      BASE64URL_ALPHABET[(triple >> 18) & 0x3f],
      BASE64URL_ALPHABET[(triple >> 12) & 0x3f],
      BASE64URL_ALPHABET[(triple >> 6) & 0x3f],
      BASE64URL_ALPHABET[triple & 0x3f],
    ];

    // base64url carries no `=` padding, so the tail is truncated to the number
    // of characters the remaining bytes actually encode: 2 for one byte, 3 for
    // two. Emitting the full quartet would decode to trailing NULs.
    const kept = i + 2 < input.length ? 4 : i + 1 < input.length ? 3 : 2;
    out += chars.slice(0, kept).join('');
  }

  return out;
}

/** The inverse of {@link toBase64Url}. `null` for anything that is not one. */
function fromBase64Url(input: string): string | null {
  if (input.length === 0) {
    return null;
  }

  let bits = 0;
  let bitCount = 0;
  let out = '';

  for (const char of input) {
    const value = BASE64URL_ALPHABET.indexOf(char);
    if (value < 0) {
      return null;
    }

    bits = (bits << 6) | value;
    bitCount += 6;

    if (bitCount >= 8) {
      bitCount -= 8;
      out += String.fromCharCode((bits >> bitCount) & 0xff);
    }
  }

  return out;
}

/**
 * Turns a row id into the opaque token a client hands back unchanged.
 *
 * Opaque so that the shape can change without a client having built one by hand
 * — the cursor is a promise about *ordering*, not about ids, and the day a list
 * needs a composite key nothing should have to be renegotiated.
 */
export function encodeIdCursor(id: string): string {
  return toBase64Url(id);
}

/**
 * Reads a cursor, or answers `null` for anything that is not one.
 *
 * **It validates the decoded value, not merely the encoding.** A client that
 * base64s something of its own making would otherwise hand a repository a string
 * Postgres refuses to compare against a `uuid`, and an ordinary scroll would
 * answer 500. `null` here becomes a 400 at the edge of the request (see
 * {@link CursorSchema}), which is what it is.
 */
export function decodeIdCursor(token: string): string | null {
  const decoded = fromBase64Url(token);
  if (decoded === null) {
    return null;
  }

  const parsed = UuidSchema.safeParse(decoded);

  return parsed.success ? parsed.data : null;
}

/**
 * A cursor as it appears in a **request**: refused at the edge if it is not one,
 * so no service ever holds a token it has to re-check.
 */
export const CursorSchema = z.string().refine((token) => decodeIdCursor(token) !== null, {
  message: 'Not a valid cursor.',
});

/**
 * Builds a cursor-paginated query schema: `before` + `limit`, `.strict()`, plus
 * whatever whitelisted filters the resource declares (N7).
 *
 * `before`, not `after`: these lists are read **backwards** — a chat opens at the
 * newest message and scrolls into the past — so the direction is named for what
 * the member is doing.
 */
export const CursorQuerySchema = z.object({
  before: CursorSchema.optional(),
  // Optional, never defaulted — see `DEFAULT_CURSOR_LIMIT`. The ceiling is
  // still enforced here, because a bound a client can exceed is not a bound.
  limit: z.coerce.number().int().min(1).max(MAX_CURSOR_LIMIT).optional(),
});

/** The cursor keys every cursor query already carries. */
const RESERVED_CURSOR_KEYS = Object.keys(CursorQuerySchema.shape);

export function makeCursorQuery<TFilters extends z.ZodRawShape = Record<never, never>>(
  filters: TFilters = {} as TFilters,
) {
  for (const key of Object.keys(filters)) {
    if (RESERVED_CURSOR_KEYS.includes(key)) {
      throw new Error(
        `makeCursorQuery: filter "${key}" collides with a reserved cursor key (${RESERVED_CURSOR_KEYS.join(', ')}).`,
      );
    }
  }

  return CursorQuerySchema.extend(filters).strict();
}

/** The meta block a cursor page carries. There is no total and no page number. */
export const CursorPageMetaSchema = z
  .object({
    /** Where the next page starts, or `null` when this was the last one. */
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  })
  .strict();

export type CursorPageMeta = z.infer<typeof CursorPageMetaSchema>;

/** Wraps an item schema in the standard cursor-page payload shape. */
export function makeCursorPageSchema<TItem extends z.ZodType>(item: TItem) {
  return z.object({ items: z.array(item), meta: CursorPageMetaSchema });
}

export type CursorPage<TItem> = {
  items: TItem[];
  meta: CursorPageMeta;
};

/**
 * Turns an **over-fetched** result into the page that goes out.
 *
 * ## The over-fetch is the contract, and it is why this is one function
 *
 * There is no honest way to answer "is there another page" from a result of
 * exactly `limit` rows: it is the same array whether one more row exists or
 * none does. The alternatives are a second `count(*)` on every scroll, or asking
 * for **`limit + 1`** and letting the extra row answer the question. This takes
 * the second.
 *
 * That only works if the over-fetch and the trim stay together. Split across a
 * repository that fetches and a mapper that reports, the pair drifts the first
 * time somebody "tidies" the `+ 1` — and the symptom is not a crash: the last
 * page of every conversation whose length is a multiple of the page size reports
 * `hasMore`, the client asks for the next one, and the member gets a spinner that
 * never resolves. So the repository passes what it got, says what it asked for,
 * and this decides both halves at once.
 *
 * The cursor anchors on the **last returned** row — not the dropped one, which
 * would skip it for ever, and not the first, which would re-serve the page just
 * read.
 *
 * @param fetched Up to `limit + 1` rows, already in the page's ordering.
 * @param limit The page size the client asked for.
 * @param idOf The row's ordering key.
 */
export function takeCursorPage<TItem>(
  fetched: readonly TItem[],
  limit: number,
  idOf: (item: TItem) => string,
): CursorPage<TItem> {
  const hasMore = fetched.length > limit;
  const items = hasMore ? fetched.slice(0, limit) : [...fetched];
  const last = items[items.length - 1];

  return {
    items,
    meta: {
      nextCursor: hasMore && last !== undefined ? encodeIdCursor(idOf(last)) : null,
      hasMore,
    },
  };
}
